/**
 * INTERVIEW-SHELL-1 (s205, SHELL-PILOT-1) — the interview's step model.
 *
 * The ENGINE's data shape, stated in this build's own words. 🔴 `docs/living/planner-magic-canvas/`
 * is DRAFT and binds nothing (D-193.1); it is not cited here and was not read for this file. The six
 * chapters and their order come from the charter §4A (D-200.16 / D-200.15 / D-200.20), which is ruled.
 *
 * 🔴 CONTENT IS GATED ON SPEC (D-202.22) — SPEC is one pass over all six chapters, so chapter content
 * waits on chapters five and six being drawn. This file therefore carries EXACTLY ONE real screen
 * (chapter one's pets question, the pilot's measured screen) plus its checkpoint. Every other chapter
 * holds a single `placeholder` screen so the progress bar and the step pills are REAL — a shell whose
 * pills were faked would measure nothing. Placeholder copy is marked `[SPEC copy pending]` on purpose:
 * it must be impossible to mistake it for ruled copy.
 *
 * Screen ordering within a chapter is the array order. Position is `{ chapter, step }` where chapter
 * is 1-BASED (it is shown to the planner as "Chapter N of 6") and step is a 0-based index into
 * `screens`. Both are persisted on `clients.interview_position`.
 */

import { SECTION_SLUGS } from "../constants/sections";
import { BENEFICIARIES_SLUG } from "../essentials-spec";
import { boardStep, boardTiles } from "./board";
import { BOARD_CH2, ch2Words } from "./board-ch2";
import { BOARD_CH3 } from "./board-ch3";
import { HOME_BOARD } from "../plan-home/home-board.generated";

/** What the shell renders for a screen. `question` captures; `checkpoint` reflects; `placeholder` stands in. */
export type InterviewScreenKind = "question" | "checkpoint" | "placeholder";

export type InterviewScreen = {
  /** Stable id — persisted nowhere, but used for test ids and React keys. */
  key: string;
  /** Step-pill label (§4B 17). Short: the pills sit in a row. */
  crumb: string;
  /** §4B 11: a plain noun title, never a clever question. */
  title: string;
  /** One line under the title. Says what happens, never why we designed it that way (§4B 18). */
  why?: string;
  kind: InterviewScreenKind;
  /**
   * Target section slug for facts this screen captures. Absent on checkpoints and placeholders.
   * The screen writes there through POST /generic-entries — the ONE planner write path.
   */
  section?: string;
  /**
   * The `situation-flags` fields this screen's answers write (the existing PATCH's keys, e.g.
   * `hasPets`). A list since s221 Lane 1: Children and dependants sets two (ANSWER-FLAG-WRITE-1).
   * Absent where a screen prunes nothing.
   */
  flagFields?: readonly string[];
  /**
   * s221 Lane 1d — the screen's "none" tile, as the board declares it (Main.dc.html `groups[].none`).
   * s222 BOARD-AS-SPEC-1: read from the generated board data (`./board`), never re-typed.
   * `prune` is how far the executor-task counter drops while it is picked; `delta` the sentence shown.
   */
  none?: { label: string; sub?: string; prune: number; delta?: string };
  /** s223 — the board's `nextNote`: one quiet line beside "Save and continue" (Main.dc.html, the footer row). */
  nextNote?: string;
};

export type InterviewChapter = {
  /** 1-based; matches the planner-facing "Chapter N of 6". */
  number: number;
  /** §4B 13 + D-200.20: chapters and categories share names. */
  name: string;
  /** §4B 13: ONE line to open a chapter. No chapter intros. */
  opener: string;
  screens: InterviewScreen[];
};

/**
 * s222 BOARD-AS-SPEC-1 (D-221.40) — a screen's words (crumb, title, why, the "none" answer) as the board
 * declares them. Only the words: where its answers are stored stays in the screen entry below.
 */
function boardWords(key: string): Pick<InterviewScreen, "key" | "crumb" | "title" | "why" | "none" | "nextNote"> {
  const s = boardStep(key);
  const none = boardTiles(s).none;
  return { key: s.key, crumb: s.crumb, title: s.title, why: s.why, ...(none ? { none } : {}), ...(s.nextNote ? { nextNote: s.nextNote } : {}) };
}

/** s228 — a chapter's name, as board 12 (the plan's sidebar) names it. Throws if the board lost the chapter. */
function boardChapterName(n: number): string {
  const title = HOME_BOARD.chapters.find((c) => c.number === n)?.title;
  if (!title) throw new Error(`chapters: board 12 names no chapter ${n}`);
  return title;
}

/**
 * s232 — two of chapter two's board keys cannot be product keys, the `done` → `ch1-checkpoint` precedent: `home` is
 * already the plan's own home ("/dashboard" is `current === "home"` in PlanApp, and the router's `from=home`), and the
 * closing screen is the chapter's checkpoint. Only the KEY changes; the words still come from the board.
 */
// s233 (D-233.81): the `home` step is gone (board v440) — Your home is part of "Home and property" (`prop`); `your-home`
// is retired (a saved position moves back one step on boot, CH2-POSITIONS-V440).
export const CH2_PRODUCT_KEY: Readonly<Record<string, string>> = { end: "ch2-checkpoint" };

/** s233 (D-233.81): a retired screen key → the screen that now asks its questions (an old link or bookmark still opens). */
export const RETIRED_SCREEN_KEYS: Readonly<Record<string, string>> = { "your-home": "prop" };

/** s232 PR B — chapter two's built screens, by board key, and the section each one's rows are saved to first. */
const CH2_BUILT: Readonly<Record<string, string>> = {
  prop: SECTION_SLUGS.ASSETS_PROPERTIES,
  veh: SECTION_SLUGS.ASSETS_VEHICLE_LIST,
  val: SECTION_SLUGS.ASSETS_VALUABLES,
  // s232 PR C: a firearm is a Valuables row (ruling 3); Safes is its own hidden section (ruling 4).
  fa: SECTION_SLUGS.ASSETS_VALUABLES,
  safes: SECTION_SLUGS.ASSETS_SAFES_LOCKED_PLACES,
};

/**
 * s237 (chapter three PR 2) — chapter three's screens, as board 3 (Chapter3.dc.html) declares them: keys, crumbs, titles,
 * why lines and the none tile from the generated data (./board-ch3), never typed here. Work is built on the shared
 * tile-to-row engine (D-236.61); Coverage, Past employers and the checkpoint stay `placeholder` until PR 3. The checkpoint's
 * board key is `end3` (canvas v478 — screen keys are unique across boards); its product key is `ch3-checkpoint`.
 */
export const CH3_PRODUCT_KEY: Readonly<Record<string, string>> = { end3: "ch3-checkpoint" };
/** Chapter three's built screens, by board key, and the section each one's rows are saved to first. */
const CH3_BUILT: Readonly<Record<string, string>> = { work: SECTION_SLUGS.PERSONAL_INFORMATION_CAREER };
function ch3Words(key: string): Pick<InterviewScreen, "key" | "crumb" | "title" | "why" | "none" | "nextNote"> {
  const s = BOARD_CH3.find((x) => x.key === key);
  if (!s) throw new Error(`chapters: board 3 has no screen "${key}"`);
  const none = s.groups?.[0]?.none;
  return { key: s.key, crumb: s.crumb, title: s.title, why: s.why, ...(none ? { none } : {}), ...(s.nextNote ? { nextNote: s.nextNote } : {}) };
}

/** A chapter that is drawn but not yet specced — one honest placeholder, so its pill is real. */
function pendingChapter(number: number, name: string): InterviewChapter {
  return {
    number,
    name,
    opener: "[SPEC copy pending]",
    screens: [
      {
        key: `ch${number}-pending`,
        crumb: "Coming soon",
        title: `${name} — [SPEC copy pending]`,
        // s232 (item 3): no decision code in planner-facing words.
        why: "[SPEC copy pending] — this chapter's screens are written once all six are drawn.",
        kind: "placeholder",
      },
    ],
  };
}

/**
 * The six chapters, in the ruled order (charter §4A, D-200.16 as AMENDED by D-224.j):
 *   1 You and your people → 2 What you own → 3 Work → 4 Money and insurance →
 *   5 Legal and documents → 6 Your wishes
 *
 * 🔴 s228 (Reade: "Board 12's chapter names win everywhere"): the NAMES are board 12's (`HOME_BOARD.chapters`), read
 * here — never typed — so the shell's "Chapter N · name" and the plan's sidebar say the same thing. This supersedes
 * D-200.20's names ("Home & Property", "Money & Insurance", "Legal & Documents", "Your Wishes").
 *
 * 🔴 s228: D-224.j ("The boards were right and the shell was wrong") moved Work ahead of Money — chapter four's
 * boards carry insurance "in from chapter three" and chapter five's the business "named in chapter three". This file
 * still had the s200 order until PLAN-HOME-1 put the chapters in the sidebar beside board 12's.
 *
 * 🔴 Work sits ahead of Legal on a DEPENDENCY, not a preference — Legal holds business documents and
 * Work is where the interview learns a business exists. Do not reorder without re-reading D-200.15.
 */
export const INTERVIEW_CHAPTERS: InterviewChapter[] = [
  {
    number: 1,
    name: boardChapterName(1),
    // The one-line opener, on the chapter's first screen only (§4B 13).
    opener: "About five minutes, all from memory — nothing to go and find.",
    screens: [
      // s223 / s233 — Personal details, the board's `you` step (drawn by its template; words in ./board-template).
      // s233 (D-233.68, D-233.75): the old `you` and `facts` steps are ONE screen — the name, the home address, the
      // birth details, the phone, the email; About You fields on the card row. The email is the signup address,
      // read-only. The Province box is gone (the home address carries it; PROVINCE-FROM-ADDRESS-1).
      {
        ...boardWords("you"),
        kind: "question",
        section: SECTION_SLUGS.ABOUT_YOU,
      },
      // s221 Lane 1 — the SPEC board's `kids` and `daily` steps (D-221.5); since s222 their words come from
      // the generated board data. The other seven chapter-one screens land in Lane 2, in the board's order.
      {
        ...boardWords("kids"),
        kind: "question",
        section: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW,
        flagFields: ["hasChildren", "hasDependents"],
      },
      {
        ...boardWords("daily"),
        kind: "question",
        // D-221.10: the day's answers live on the SAME rows the screen before created (rule 121).
        section: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW,
      },
      // 🔴 s224 (RULE 137, D-223.34) — the s205 pilot hand-typed its own title and why line here, and
      // BOTH drifted from the board: "Pets and animals" against the board's "Pets or Other Animals",
      // and a why line the board never carried. The words come from the board now, like every other
      // screen; the storage and the flag stay here, which is the half a board does not carry.
      {
        ...boardWords("pets"),
        kind: "question",
        section: SECTION_SLUGS.FAMILY_PETS,
        flagFields: ["hasPets"],
      },
      // s223 — the board's `exec` step (D-222.10 part 3). Rows are legal-your-executor records; the tile is the
      // row's stored standing (`told`). The executor-access sync on that section is left exactly as it is.
      {
        ...boardWords("exec"),
        kind: "question",
        section: SECTION_SLUGS.LEGAL_YOUR_EXECUTOR,
      },
      // s223 - the board's `benef` step (D-222.10 part 4). Rows are `beneficiaries` records; the tile is stored,
      // "Under 18?" is asked rather than derived, and the planner's own children carry in from chapter one.
      {
        ...boardWords("benef"),
        kind: "question",
        section: BENEFICIARIES_SLUG,
      },
      // s223 - the board's `ids` step (D-222.10 part 5). A tile pick unhides the matching locked built-in row of
      // personal-information-government-ids; un-picking hides it. The number itself is Category A and never asked.
      {
        ...boardWords("ids"),
        kind: "question",
        section: SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS,
      },
      // s223 RULE 137 — the checkpoint READS the board (`done`), it does not keep its own copy. It had
      // hardcoded "Where that leaves things" where board 1 says "That is chapter one.", and dropped the
      // board's why line entirely. The KEY stays `ch1-checkpoint` because the router and the saved
      // `interview_position` use it; only the words change source.
      {
        ...boardWords("done"),
        key: "ch1-checkpoint",
        kind: "checkpoint",
      },
    ],
  },
  // s232 (CHAPTER-TWO-ENGINE-1, PR A; D-231.93) — chapter two's screens, as board 2 (Chapter2.dc.html) declares them:
  // their keys, crumbs, titles and why lines come from the generated board data (./board-ch2), never typed here.
  // s232 PR B: Your home, Other property, Vehicles and Valuables are built (./board-ch2 words; storage beside each
  // screen, client/src/components/interview/ch2). Firearms, Safes and the close stay `placeholder` until PR C — the
  // board's title and why line only (item 3). 🔴 The home's title carries the board's stand-in address ("12 Maple
  // Street, Toronto" — rule 75); the shell shows the planner's own home in its place (ch2 `homeTitle`).
  {
    number: 2,
    name: boardChapterName(2),
    // Not shown (InterviewShell, s221 Lane 1c fix 3) and not drawn on the board — so not written here either.
    opener: "",
    screens: BOARD_CH2.map((s) => ({
      ...ch2Words(s.key),
      key: CH2_PRODUCT_KEY[s.key] ?? s.key,
      ...(CH2_BUILT[s.key] ? { kind: "question" as const, section: CH2_BUILT[s.key] } : { kind: "placeholder" as const }),
    })),
  },
  {
    number: 3,
    name: boardChapterName(3),
    opener: "",
    screens: BOARD_CH3.map((s) => ({
      ...ch3Words(s.key),
      key: CH3_PRODUCT_KEY[s.key] ?? s.key,
      ...(CH3_BUILT[s.key] ? { kind: "question" as const, section: CH3_BUILT[s.key] } : { kind: "placeholder" as const }),
    })),
  },
  pendingChapter(4, boardChapterName(4)),
  pendingChapter(5, boardChapterName(5)),
  pendingChapter(6, boardChapterName(6)),
];

export const INTERVIEW_CHAPTER_COUNT = INTERVIEW_CHAPTERS.length;

/**
 * s228 (PLAN-HOME-1, Reade's ruling D) — FINAL TOUCHES: the five after-interview steps of board 7 (`Extras.dc.html`),
 * listed in the plan's sidebar under "Final touches". Reade: "unnumbered, each opens (today's placeholder), and none
 * counts towards finishing the interview. Never use the word 'optional' in the product for this section."
 *
 * 🔴 They are NOT chapters and never join `INTERVIEW_CHAPTERS`: the interview stays six chapters, so `nextPosition`,
 * the progress bar and completion are untouched. Their names are board 12's (`HOME_BOARD.touches`), never typed here.
 */
export const FINAL_TOUCHES: readonly InterviewScreen[] = HOME_BOARD.touches.map((t) => ({
  key: `touch-${t.id}`,
  crumb: t.title,
  title: `${t.title} — [SPEC copy pending]`,
  why: "[SPEC copy pending] — this step is built after the six chapters.",
  kind: "placeholder" as const,
}));

/** A screen's declaration by key (chapter one's screens are unique by key). */
export function screenByKey(key: string): InterviewScreen | undefined {
  for (const c of INTERVIEW_CHAPTERS) {
    const s = c.screens.find((x) => x.key === key);
    if (s) return s;
  }
  return undefined;
}

/** Total screens across every chapter — the progress bar's denominator. */
export const INTERVIEW_TOTAL_STEPS = INTERVIEW_CHAPTERS.reduce((n, c) => n + c.screens.length, 0);

export type InterviewPosition = { chapter: number; step: number };

export const INTERVIEW_START: InterviewPosition = { chapter: 1, step: 0 };

/** The chapter at a position, or undefined if the position is out of range. */
export function chapterAt(pos: InterviewPosition): InterviewChapter | undefined {
  return INTERVIEW_CHAPTERS.find((c) => c.number === pos.chapter);
}

/** The screen at a position, or undefined if either index is out of range. */
export function screenAt(pos: InterviewPosition): InterviewScreen | undefined {
  return chapterAt(pos)?.screens[pos.step];
}

/**
 * Clamp an arbitrary stored position onto a real screen.
 *
 * 🔴 Needed because the position OUTLIVES the chapter model: a planner parked on chapter 3 step 4
 * when SPEC lands may find that chapter rewritten with fewer screens, and a stored cursor pointing
 * past the end must resolve to a screen rather than a blank surface. Falls back to the start.
 */
export function clampPosition(pos: InterviewPosition | null | undefined): InterviewPosition {
  if (!pos) return INTERVIEW_START;
  const chapter = INTERVIEW_CHAPTERS.find((c) => c.number === pos.chapter);
  if (!chapter) return INTERVIEW_START;
  const step = Math.min(Math.max(pos.step, 0), chapter.screens.length - 1);
  return { chapter: chapter.number, step };
}

/** 0-based index of a position across ALL chapters — drives the progress bar. */
export function absoluteStepIndex(pos: InterviewPosition): number {
  let n = 0;
  for (const c of INTERVIEW_CHAPTERS) {
    if (c.number === pos.chapter) return n + pos.step;
    n += c.screens.length;
  }
  return 0;
}

/** The next position, or `null` when the planner is on the last screen of the last chapter. */
export function nextPosition(pos: InterviewPosition): InterviewPosition | null {
  const chapter = chapterAt(pos);
  if (!chapter) return INTERVIEW_START;
  if (pos.step + 1 < chapter.screens.length) return { chapter: pos.chapter, step: pos.step + 1 };
  const next = INTERVIEW_CHAPTERS.find((c) => c.number === pos.chapter + 1);
  return next ? { chapter: next.number, step: 0 } : null;
}

/** The previous position, or `null` when the planner is on the very first screen. */
export function previousPosition(pos: InterviewPosition): InterviewPosition | null {
  if (pos.step > 0) return { chapter: pos.chapter, step: pos.step - 1 };
  const prev = INTERVIEW_CHAPTERS.find((c) => c.number === pos.chapter - 1);
  return prev ? { chapter: prev.number, step: prev.screens.length - 1 } : null;
}
